/**
 * /en/virtual-office — English pillar for registered-office address and VAT
 * place of business. Independently written English copy (not a translation).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { absoluteAsset, absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { VO_SERVICES } from "@/data/virtual-office/axes";
import { AnswerFirst } from "@/components/AnswerFirst";
import { FirmCta } from "@/components/firm/FirmCta";
import { ContentFreshness } from "@/components/ContentFreshness";
import teamAsset from "@/assets/team-group.png.asset.json";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  howToSchema,
  faqPageSchema,
} from "@/lib/schema";

const PATH = "/virtual-office";
const TITLE = "Virtual Office in Thailand for Company & VAT Registration";
const DESC =
  "How a serviced registered address works for Thai company incorporation and VAT registration: the consent letter, the documents officers ask for, the site inspection, and the mistakes that send an application back for correction.";

const ANSWER =
  "A virtual office in Thailand is a serviced address you are entitled to use as your company's registered office. It works for incorporation when the address can be stated in full — room, floor, building, road, sub-district, district, postcode — and the owner signs a consent letter with proof of title or lease. It works for VAT registration when signage shows the taxpayer's name and someone can receive the Revenue Department's site inspection during business hours.";

const SECTIONS: Array<{ h: string; body: string; bullets: string[] }> = [
  {
    h: "Two separate approvals, two separate standards",
    body: "Incorporation and VAT registration are decided by different agencies and judged on different things. The Department of Business Development checks that the address exists and that the person who consented has the right to give that consent. The Revenue Department goes further: an officer may visit and look for a real place of business.",
    bullets: [
      "Incorporation: address stated in full, consent letter, proof of title or lease, a simple location map.",
      "VAT: the same file plus signage bearing the taxpayer name, photographs, and a person on site who can answer questions about the business.",
      "A provider who accepts incorporation does not automatically accept VAT registration — confirm this in writing before you pay.",
    ],
  },
  {
    h: "What foreign-owned companies should settle first",
    body: "For a company with foreign shareholders, the address is rarely the bottleneck. Corporate documents issued abroad and the activity list are.",
    bullets: [
      "Check whether the intended activity falls under the restrictions of the law on foreign business operation before drafting anything.",
      "Foreign corporate shareholder documents normally need certification in the form Thai agencies accept, plus a certified Thai translation.",
      "Spell every name identically across passport, power of attorney and translation; a mismatch means a further round of documents.",
      "If foreign staff will be employed, plan for the work-permit stage now — officers look for workspace consistent with the positions applied for.",
    ],
  },
  {
    h: "Fees and commitments we will not put on a web page",
    body: "Government fees are set by the agencies and charged as incurred; our professional fees depend on the actual scope. We quote in writing before work starts, and no adviser can promise that an agency will approve a filing — that decision belongs to the reviewing officer.",
    bullets: [
      "Ask any provider to separate government fees from service fees in the engagement document.",
      "Ask what happens at the end of the contract and how long you have to move the registered address.",
      "Ask how documents with a legal response deadline — court papers, tax assessments — are notified and how fast.",
    ],
  },
];

const FAQ = [
  {
    question: "Can I register for VAT at a virtual office in Thailand?",
    answer:
      "Yes, provided the location satisfies what the Revenue Department checks: proof of your right to use the premises, signage showing the taxpayer's name visible from outside, an identifiable area of use, and a person available for the site inspection during business hours. Some providers allow incorporation only, so confirm VAT use in the contract before paying. The reviewing officer decides the outcome.",
  },
  {
    question: "What does the officer look at during the site inspection?",
    answer:
      "Typically: that the address on the application matches the real location, that signage with the taxpayer's name is visible from outside, that there is space used for the business or for keeping records, and that someone present can explain the business. Keep the company certificate, the lease or consent letter, and material describing the business at the premises.",
  },
  {
    question: "Why are VAT applications sent back for correction?",
    answer:
      "Most often because the address differs between the application, the company certificate and the lease — even by a room number; because there is no signage; because nobody was on site when the officer visited; or because the proof of title is incomplete or the person who consented was not entitled to consent.",
  },
  {
    question: "Can I use a condominium unit as the registered office?",
    answer:
      "Legally possible with the owner's consent and proof of title, but check the condominium regulations first. Many prohibit business use or signage, which makes VAT registration difficult because you can neither display the taxpayer name nor host an inspection. Ask the juristic person for written confirmation in advance.",
  },
  {
    question: "What do I have to do when the address service ends?",
    answer:
      "Move the registered office and place of business to the new address, then file the change with the Department of Business Development and notify the Revenue Department before the contract ends. Otherwise official letters with response deadlines will be sent to an address where nobody receives them.",
  },
  {
    question: "Can several companies share one address?",
    answer:
      "In the register, yes — it is common in office buildings. For VAT the officer may ask for further evidence that each business genuinely operates, such as the specific area each occupies, separate signage and operational records. Ask the provider to state the room or area allocated to you in the consent letter.",
  },
];

export const Route = createFileRoute("/en/virtual-office")({
  loader: () => ({
    h: (() => {
      const ogImage = absoluteAsset(teamAsset.url);
       const url = absoluteUrl("/en/virtual-office", null, "en");
      return {
        meta: [
          { title: ctrTitle(TITLE) },
          { name: "description", content: ctrDesc(DESC) },
          { property: "og:title", content: ctrTitle(TITLE) },
          { property: "og:description", content: ctrDesc(DESC) },
          { property: "og:url", content: url },
          { property: "og:type", content: "article" },
          { property: "og:image", content: ogImage },
          { property: "og:image:alt", content: "Thai Notary Law & Service team" },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:image", content: ogImage },
        ],
        links: [
          { rel: "canonical", href: url },
          ...canonicalAndHreflang(PATH, null, "en").filter((l) => l.rel === "alternate"),
        ],
        scripts: [
          jsonLdScript(
            buildJsonLd([
              webPageSchema({ url: `/en${PATH}`, title: TITLE, description: DESC, inLanguage: "en" }),
              breadcrumbListSchema([
                { name: "Home", url: absoluteUrl("/", null, "en") },
                { name: "Virtual office & VAT registration", url },
              ]),
              ...VO_SERVICES.map((s) =>
                legalServiceSchema({
                  name: s.nameEn,
                  description: s.tagline,
                  url: `/en${PATH}`,
                  areaServed: "TH",
                  serviceType: s.nameEn,
                }),
              ),
              howToSchema({
                name: "Registering a Thai company and VAT using a serviced address",
                description: DESC,
                steps: [
                  { name: "Step 1", text: "Confirm the premises may be used as a juristic person's registered office." },
                  { name: "Step 2", text: "Reserve the company name and prepare the application and objectives." },
                  { name: "Step 3", text: "Obtain the consent letter and the owner's proof of title or lease." },
                  { name: "Step 4", text: "File the incorporation application and collect the company certificate." },
                  { name: "Step 5", text: "Install signage with the taxpayer name and prepare photographs and a location map." },
                  { name: "Step 6", text: "File the VAT application and host the site inspection during business hours." },
                ],
              }),
              faqPageSchema(FAQ),
            ]),
          ),
        ],
      };
    })(),
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: EnVirtualOffice,
});

function EnVirtualOffice() {
  return (
    <>
      <header
        className="border-b border-border/60 text-[var(--primary-foreground)]"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-14">
          <nav aria-label="Breadcrumb" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/en" className="hover:underline">
              Home
            </Link>{" "}
            <span aria-hidden>/</span> Virtual office &amp; VAT registration
          </nav>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight md:text-5xl">
            Virtual office in Thailand for company and VAT registration
          </h1>
          <p className="mt-5 max-w-3xl leading-relaxed text-[var(--primary-foreground)]/85">
            A registered address is a legal record, not just a mailing point. This page sets out what the two
            agencies actually check, what you must be able to produce, and where applications lose time.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <AnswerFirst>{ANSWER}</AnswerFirst>
        <p className="mt-4 text-xs text-muted-foreground">
          Reviewed October 2026. Registration requirements and the documents officers request can change — confirm
          with the Department of Business Development and the Revenue Area Office covering your premises before you
          file. We do not publish service fees; ask for a written quotation by phone, LINE or e-mail.
        </p>

        {SECTIONS.map((s) => (
          <section key={s.h} className="border-t border-border/60 py-10">
            <h2 className="text-2xl font-bold text-foreground">{s.h}</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{s.body}</p>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
              {s.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">Service lines</h2>
          <ul className="mt-4 space-y-5">
            {VO_SERVICES.map((s) => (
              <li key={s.slug} className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-semibold text-foreground">{s.nameEn}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.tagline}</p>
                <p className="mt-3 text-sm">
                  <Link
                    to="/virtual-office/$service"
                    params={{ service: s.slug }}
                    className="text-primary hover:underline"
                  >
                    Read the detailed Thai page — documents, steps, comparison table →
                  </Link>
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-2xl font-bold text-foreground">Frequently asked questions</h2>
          <dl className="mt-6 space-y-6">
            {FAQ.map((f) => (
              <div key={f.question} className="rounded-xl border border-border bg-card p-6">
                <dt className="font-semibold text-foreground">{f.question}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-xl font-bold text-foreground">Related</h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link to="/en/practice/$area" params={{ area: "corporate" }} className="text-primary hover:underline">
                Corporate and commercial practice
              </Link>
            </li>
            <li>
              <Link to="/en/practice/$area" params={{ area: "tax" }} className="text-primary hover:underline">
                Tax practice
              </Link>
            </li>
            <li>
              <Link to="/virtual-office" className="text-primary hover:underline">
                อ่านหน้านี้เป็นภาษาไทย →
              </Link>
            </li>
          </ul>
        </section>

        <section className="border-t border-border/60 py-10">
          <h2 className="text-xl font-bold text-foreground">Official sources</h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <a
                href="https://www.dbd.go.th/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Department of Business Development
              </a>{" "}
              — incorporation procedure and registered-address changes.
            </li>
            <li>
              <a
                href="https://www.rd.go.th/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                The Revenue Department
              </a>{" "}
              — VAT registration conditions and tax-invoice requirements.
            </li>
          </ul>
        </section>

        <ContentFreshness path="/en/virtual-office" locale="en" reviewer="Thai corporate and tax-law content reviewer" />
      </div>

      <FirmCta
        heading="Talk to us about your registered address"
        body="Tell us the business activity and the area you have in mind. We will list the documents to prepare, the order of filings, and the points that usually cause a correction round — with scope and fees confirmed in writing before work starts."
      />
    </>
  );
}
